import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Input } from '@/shared/ui/inputs/Input';
import { Textarea } from '@/shared/ui/inputs/Textarea';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { apiWarehouseProductPatch, apiWarehouseProductPost } from '@/entities/warehouse';
import { useCargoIdLookupByCode } from '@/entities/cargo-id';
import {
  warehouseProductSchema,
  type WarehouseProductFormValues,
} from '@/features/warehouse/modules/types';
import type {
  ApiWarehouseProductItem,
  ApiWarehouseProductPostBody,
} from '@/shared/types/api/warehouse';

type Props = {
  mode: 'create' | 'edit';
  product?: ApiWarehouseProductItem | null;
};

const num = (v: number | undefined): number | undefined =>
  v === undefined || Number.isNaN(v) ? undefined : v;

const str = (v: string | undefined): string | undefined => {
  const trimmed = v?.trim();
  return trimmed ? trimmed : undefined;
};

const toBody = (v: WarehouseProductFormValues): ApiWarehouseProductPostBody => ({
  trek_id: v.trek_id.trim(),
  name: str(v.name),
  quantity: num(v.quantity),
  price: num(v.price),
  weight: num(v.weight),
  store_name: str(v.store_name),
  cargo_id_code: str(v.cargo_id_code),
  address: str(v.address),
  note: str(v.note),
});

const isCargoIdMessage = (msg: string): boolean => /cargo\s*id/i.test(msg);

// Mirror the heuristic used by ScannerDialog so the form surfaces the same
// friendly duplicate message owners see when scanning. Owner rule: never
// leak the technical `trek_id:` field name in the user-facing toast.
const looksLikeDuplicateTrekId = (msg: string): boolean => {
  const lower = msg.toLowerCase();
  if (lower.includes('ownerless product with this trek id')) return true;
  if (!lower.includes('trek')) return false;
  return (
    lower.includes('already exists') ||
    lower.includes('already') ||
    lower.includes('duplicate') ||
    lower.includes('mavjud') ||
    lower.includes('avval') ||
    lower.includes('уже') ||
    lower.includes('существует')
  );
};

export const WidgetsWarehouseFormMain = ({ mode, product = null }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const isEdit = mode === 'edit';

  const form = useForm<WarehouseProductFormValues>({
    resolver: zodResolver(warehouseProductSchema),
    defaultValues: {
      trek_id: product?.trek_id ?? '',
      name: product?.name ?? '',
      quantity: product?.quantity ?? undefined,
      price: product?.price != null ? Number(product.price) : undefined,
      weight: product?.weight != null ? Number(product.weight) : undefined,
      store_name: product?.store_name ?? '',
      cargo_id_code: product?.cargo_id_code ?? '',
      address: product?.address ?? '',
      note: product?.note ?? '',
    },
  });

  // When the edit page transitions from loading to loaded, sync the form.
  useEffect(() => {
    if (!isEdit) return;
    form.reset({
      trek_id: product?.trek_id ?? '',
      name: product?.name ?? '',
      quantity: product?.quantity ?? undefined,
      price: product?.price != null ? Number(product.price) : undefined,
      weight: product?.weight != null ? Number(product.weight) : undefined,
      store_name: product?.store_name ?? '',
      cargo_id_code: product?.cargo_id_code ?? '',
      address: product?.address ?? '',
      note: product?.note ?? '',
    });
  }, [isEdit, product, form]);

  const goBack = (): void => {
    navigate('/warehouse');
  };

  const handleMutationError = (err: Error): void => {
    const message = extractErrorMessage(err);
    // Friendly localized toast for the most common server validation: trying
    // to register a trek_id that already exists. Surface the same message
    // inline on the trek_id field so the user knows where to fix it.
    if (looksLikeDuplicateTrekId(message)) {
      const friendly = t('warehouse.toast.duplicateTrekId');
      form.setError('trek_id', { type: 'server', message: friendly });
      toast.error(friendly);
      return;
    }
    if (isCargoIdMessage(message)) {
      form.setError('cargo_id_code', { type: 'server', message });
    }
    toast.error(message);
  };

  const create = useMutate({
    api: (v: WarehouseProductFormValues) => apiWarehouseProductPost(toBody(v)),
    invalidateKeys: [
      queryKeys.warehouseProducts.all,
      queryKeys.warehouse.all,
      queryKeys.parcels.all,
    ],
    onSuccess: () => {
      toast.success(t('warehouse.toast.created'));
      navigate('/warehouse');
    },
    onError: (e) => handleMutationError(e),
  });

  const update = useMutate({
    api: (v: WarehouseProductFormValues) =>
      apiWarehouseProductPatch(product?.id ?? '', toBody(v)),
    invalidateKeys: [
      queryKeys.warehouseProducts.all,
      queryKeys.warehouse.all,
      queryKeys.parcels.all,
    ],
    onSuccess: () => {
      toast.success(t('warehouse.toast.updated'));
      navigate('/warehouse');
    },
    onError: (e) => handleMutationError(e),
  });

  const isPending = create.isPending || update.isPending;

  const onSubmit = form.handleSubmit((values) => {
    if (isEdit) update.mutate(values);
    else create.mutate(values);
  });

  // Auto-resolve the customer's full name (FIO) from the typed Cargo ID code.
  // The lookup hook is enabled only while the code is non-empty, so an empty
  // field fires no request. An unknown code surfaces as "not found" without
  // blocking submit.
  const cargoIdCodeValue = (form.watch('cargo_id_code') ?? '').trim();
  const fioLookup = useCargoIdLookupByCode(cargoIdCodeValue);
  const resolvedFio = fioLookup.data?.full_name?.trim() ?? '';
  const fioIsLoading = cargoIdCodeValue.length > 0 && fioLookup.isFetching;
  const fioNotFound =
    cargoIdCodeValue.length > 0 && !fioLookup.isFetching && resolvedFio.length === 0;

  const trekIdError = form.formState.errors.trek_id?.message;
  const cargoIdCodeError = form.formState.errors.cargo_id_code?.message;
  const noteError = form.formState.errors.note?.message;

  // Field-level errors from zod use i18n keys; server errors are raw messages.
  const translateFieldError = (msg: string | undefined): string | undefined => {
    if (!msg) return undefined;
    return msg.includes(' ') ? msg : t(msg);
  };

  const title = isEdit
    ? t('warehouse.formPage.editTitle')
    : t('warehouse.formPage.createTitle');

  return (
    <>
      <PageHeader title={title} showBack backTo="/warehouse" />
      <form
        onSubmit={onSubmit}
        className="space-y-4 px-4 py-4 lg:px-6 lg:py-6"
        data-cy="warehouse-form"
      >
        <div className="grid grid-cols-1 gap-3">
          {(() => {
            // Auto-upper-case the trek_id input both in the DOM and in
            // RHF state so a barcode scanner spitting lowercase chars
            // still produces a canonical, upper-case value.
            const trekIdReg = form.register('trek_id', {
              setValueAs: (v: unknown) =>
                typeof v === 'string' ? v.toUpperCase() : '',
            });
            return (
              <Input
                label={t('warehouse.form.fields.trekId')}
                required
                autoFocus
                autoCapitalize="characters"
                error={translateFieldError(trekIdError)}
                {...trekIdReg}
                onChange={(e) => {
                  const upper = e.target.value.toUpperCase();
                  if (e.target.value !== upper) e.target.value = upper;
                  form.setValue('trek_id', upper, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              />
            );
          })()}
          {(() => {
            // Auto-upper-case the Cargo ID code, same pattern as trek_id, so
            // typing lowercase still produces a canonical, upper-case value.
            const cargoIdReg = form.register('cargo_id_code', {
              setValueAs: (v: unknown) =>
                typeof v === 'string' ? v.toUpperCase() : '',
            });
            return (
              <Input
                label={t('warehouse.form.fields.cargoIdCode')}
                placeholder={t('warehouse.form.fields.cargoIdCodePlaceholder')}
                autoCapitalize="characters"
                inputMode="text"
                error={translateFieldError(cargoIdCodeError)}
                {...cargoIdReg}
                onChange={(e) => {
                  const upper = e.target.value.toUpperCase();
                  if (e.target.value !== upper) e.target.value = upper;
                  form.setValue('cargo_id_code', upper, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              />
            );
          })()}
          {/* Auto-resolved customer full name (FIO), read-only. Mirrors the
              Input primitive's shell so it reads as a disabled field. */}
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink-soft">
              {t('warehouse.form.fio.label')}
            </span>
            <div className="flex min-h-[42px] items-center rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm shadow-sm">
              {fioIsLoading ? (
                <span className="text-ink-muted">{t('warehouse.form.fio.loading')}</span>
              ) : resolvedFio ? (
                <span className="text-ink">{resolvedFio}</span>
              ) : fioNotFound ? (
                <span className="text-ink-muted">{t('warehouse.form.fio.notFound')}</span>
              ) : (
                <span className="text-ink-faint">{t('warehouse.form.fio.empty')}</span>
              )}
            </div>
          </div>
          <Textarea
            label={t('warehouse.form.fields.note')}
            rows={3}
            error={translateFieldError(noteError)}
            {...form.register('note')}
          />
        </div>

        <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:-mx-6 lg:px-6">
          <div className="mx-auto flex max-w-3xl gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              disabled={isPending}
              block
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={isPending} block>
              {t('common.save')}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};
